/**
 * 统一格式化工具（UI-SPEC-001 / UI-TOKENS.md）
 *
 * 原则：只改变「展示形态」，绝不改变原始数值本身。
 * 所有函数对 null / undefined / '' / 非数字均安全返回占位符。
 *
 * 规范见 docs/UI-REFACTOR-SPEC.md §9.2 ~ §9.4
 */

/** 空值统一占位符（em dash） */
export const EMPTY_PLACEHOLDER = '—'

/**
 * 判断是否为空值
 * @param {*} value
 * @returns {boolean}
 */
export function isEmptyValue (value) {
  if (value === null || value === undefined || value === '') return true
  if (typeof value === 'number' && isNaN(value)) return true
  return false
}

/**
 * 空值占位
 * @param {*} value
 * @param {string} [placeholder]
 * @returns {string}
 */
export function formatEmpty (value, placeholder = EMPTY_PLACEHOLDER) {
  return isEmptyValue(value) ? placeholder : value
}

/**
 * 数字千分位格式化
 * @param {number|string} value
 * @param {number} [decimals=2] 小数位数
 * @returns {string}
 */
export function formatNumber (value, decimals = 2) {
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''))
  if (isNaN(num)) return EMPTY_PLACEHOLDER
  const fixed = Math.abs(num).toFixed(decimals)
  const parts = fixed.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const sign = num < 0 ? '-' : ''
  return sign + parts.join('.')
}

/**
 * 金额格式化：¥1,234.56
 * @param {number|string} value
 * @param {object} [options]
 * @param {string} [options.symbol='¥'] 币种符号
 * @param {number} [options.decimals=2] 小数位数
 * @param {boolean} [options.blankZero=false] 0 是否显示为占位符
 * @returns {string}
 */
export function formatMoney (value, options = {}) {
  const { symbol = '¥', decimals = 2, blankZero = false } = options
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''))
  if (isNaN(num)) return EMPTY_PLACEHOLDER
  if (blankZero && num === 0) return EMPTY_PLACEHOLDER
  return symbol + formatNumber(num, decimals)
}

/**
 * 大数金额转「万 / 亿」，用于 KPI 卡片
 * @param {number|string} value
 * @param {object} [options]
 * @param {number} [options.decimals=2]
 * @returns {string}
 */
export function formatMoneyCompact (value, options = {}) {
  const { decimals = 2 } = options
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''))
  if (isNaN(num)) return EMPTY_PLACEHOLDER
  const abs = Math.abs(num)
  if (abs >= 100000000) return (num / 100000000).toFixed(decimals) + '亿'
  if (abs >= 10000) return (num / 10000).toFixed(decimals) + '万'
  return formatNumber(num, decimals)
}

/**
 * 百分比格式化
 * @param {number|string} value 已经是百分数的数值（如 95.2 表示 95.2%）
 * @param {object} [options]
 * @param {number} [options.decimals=2]
 * @returns {string}
 */
export function formatPercent (value, options = {}) {
  const { decimals = 2 } = options
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const num = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(num)) return EMPTY_PLACEHOLDER
  return num.toFixed(decimals) + '%'
}

/**
 * 日期格式化：YYYY-MM-DD
 * 复用 src/utils/dateUtil.js 的 add0，保证全站补零口径一致。
 * @param {Date|string|number} value
 * @returns {string}
 */
export function formatDate (value) {
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return String(value)
  const y = date.getFullYear()
  const m = date.getMonth() + 1
  const d = date.getDate()
  return y + '-' + (m < 10 ? '0' + m : m) + '-' + (d < 10 ? '0' + d : d)
}

/**
 * 日期时间格式化：YYYY-MM-DD HH:mm
 * @param {Date|string|number} value
 * @returns {string}
 */
export function formatDateTime (value) {
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return String(value)
  const h = date.getHours()
  const mi = date.getMinutes()
  return formatDate(date) + ' ' + (h < 10 ? '0' + h : h) + ':' + (mi < 10 ? '0' + mi : mi)
}

/**
 * 时间戳格式化：YYYY-MM-DD HH:mm:ss
 * @param {Date|string|number} value
 * @returns {string}
 */
export function formatDateTimeSec (value) {
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return String(value)
  const h = date.getHours()
  const mi = date.getMinutes()
  const s = date.getSeconds()
  return formatDate(date) + ' ' +
    (h < 10 ? '0' + h : h) + ':' +
    (mi < 10 ? '0' + mi : mi) + ':' +
    (s < 10 ? '0' + s : s)
}

/**
 * 长文本截断（表格单元格展示用，不修改原始值）
 * @param {string} value
 * @param {number} [max=30]
 * @returns {string}
 */
export function formatTruncate (value, max = 30) {
  if (isEmptyValue(value)) return EMPTY_PLACEHOLDER
  const text = String(value)
  return text.length > max ? text.slice(0, max) + '…' : text
}

export default {
  EMPTY_PLACEHOLDER,
  isEmptyValue,
  formatEmpty,
  formatNumber,
  formatMoney,
  formatMoneyCompact,
  formatPercent,
  formatDate,
  formatDateTime,
  formatDateTimeSec,
  formatTruncate
}
